import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Card } from "antd";
import { useMemo } from "react";
import { ExportToExcel } from "../../../../utils";

export const ClosingChannel = ({ data }) => {

  const closingChannelData = data?.reduce((acc, cur) => {
    const channel = cur.salesclosingchannel;
    if (channel) {
      if (!acc[channel]) {
        acc[channel] = {
          name: channel,
          value: 0,
          totalOrders: 0,
          deliveredSales: 0,
          totalProducts: 0,
          averageTicket: 0,
        };
      }
      acc[channel].value += cur.saleAmount;
      acc[channel].totalOrders += 1;
      acc[channel].averageTicket =
        acc[channel].totalProducts / acc[channel].totalOrders;
      if (cur.status === "Entregado") {
        acc[channel].deliveredSales += cur.saleAmount;
      }
      acc[channel].totalProducts += cur.products?.reduce(
        (sum, item) => sum + item.quantity,
        0
      );
    }
    return acc;
  }, {});

  const transformedData = useMemo(() => {

    const sellersSet = new Set();
    const closingChannelsSet = new Set();
    const brandsSet = new Set();
    const categoriesSet = new Set();

    data?.forEach((item) => {
      sellersSet?.add(item.seller);
      closingChannelsSet?.add(item.salesclosingchannel);
      item.products.forEach((subItem) => {
        brandsSet.add(subItem.brand);
        categoriesSet.add(subItem.category);
      });
    });

    const sellers = [...sellersSet].sort((a, b) => a.localeCompare(b));
    const closingChannels = [...closingChannelsSet].sort((a, b) => a.localeCompare(b));
    const brands = [...brandsSet].sort((a, b) => a.localeCompare(b));
    const categories = [...categoriesSet].sort((a, b) => a.localeCompare(b));

    const recordSellers = [];

    sellers?.forEach((seller)=>{
      recordSellers.push({
        "vendedor":seller
      });
    });

    const recordsClosingChannels = [];

    recordSellers?.forEach((record)=>{
      closingChannels?.forEach((closingChannel)=>{
        recordsClosingChannels.push({
          ...record,
          "canal_cierre":closingChannel
        })
      });
    });

    const recordsBrands = [];

    recordsClosingChannels?.forEach((record)=>{
      brands?.forEach((brand) => {
        recordsBrands.push({
          ...record,
          "marca":brand
        });
      });
    });

    const recordsCategories = [];

    recordsBrands?.forEach((record)=>{
      categories?.forEach((category)=>{
        recordsCategories.push({
          ...record,
          "categoria":category,
          "venta_total": 0,
          "cant_ordenes": 0,
          "cant_productos": 0,
        })
      });
    });

    data?.forEach((record)=>{
      recordsCategories?.forEach((subRecord)=>{
        if(subRecord["vendedor"] === record.seller){
          if(subRecord["canal_cierre"] === record.salesclosingchannel){
            let categoryReference = 0;
            record.products?.forEach((product)=>{
              if(subRecord["marca"]===product.brand){
                if(subRecord["categoria"] === product.category){
                  categoryReference += 1;
                  subRecord["venta_total"] += Number(product.total?.toFixed(2));
                  subRecord["cant_productos"] += product.quantity;
                }
              }
            });
            if(categoryReference > 0){
              subRecord["cant_ordenes"] += 1;
            }
          }
        }
      });
    });

    return recordsCategories;

  }, [data]);

  const transformedDataDaily = useMemo(() => {

    const sellersSet = new Set();
    const closingChannelsSet = new Set();
    const datesSet = new Set();
    const brandsSet = new Set();
    const categoriesSet = new Set();

    data?.forEach((item) => {
      const date = item.dateStart.toDate().toLocaleDateString();
      sellersSet?.add(item.seller);
      closingChannelsSet?.add(item.salesclosingchannel);
      datesSet?.add(date);
      item.products.forEach((subItem) => {
        brandsSet.add(subItem.brand);
        categoriesSet.add(subItem.category);
      });
    });

    const sellers = [...sellersSet].sort((a, b) => a.localeCompare(b));
    const closingChannels = [...closingChannelsSet].sort((a, b) => a.localeCompare(b));
    const brands = [...brandsSet].sort((a,b) => a.localeCompare(b));
    const categories = [...categoriesSet].sort((a,b)=>a.localeCompare(b));
    const dates = [...datesSet];

    const recordDatesAndSellers = [];
    dates?.forEach((date)=>{
      sellers?.forEach((seller)=>{
        recordDatesAndSellers.push({
          "fecha":date,
          "vendedor":seller
        });
      });
    });

    const recordsClosingChannels = [];

    recordDatesAndSellers?.forEach((record)=>{
      closingChannels?.forEach((closingChannel)=>{
        recordsClosingChannels.push({
          ...record,
          "canal_cierre":closingChannel
        })
      });
    });

    const recordsBrands = [];

    recordsClosingChannels?.forEach((record)=>{
      brands?.forEach((brand) => {
        recordsBrands.push({
          ...record,
          "marca":brand
        });
      });
    });

    const recordsCategories = [];

    recordsBrands?.forEach((record)=>{
      categories?.forEach((category)=>{
        recordsCategories.push({
          ...record,
          "categoria":category,
          "venta_total": 0,
          "cant_ordenes": 0,
          "cant_productos": 0,
        })
      });
    });

    data?.forEach((record)=>{
      const date = record.dateStart.toDate().toLocaleDateString();
      recordsCategories?.forEach((subRecord)=>{
        if(subRecord["fecha"] === date){
          if(subRecord["vendedor"] === record.seller){
            if(subRecord["canal_cierre"] === record.salesclosingchannel){
              let categoryReference = 0;
              record.products?.forEach((product)=>{
                if(subRecord["marca"]===product.brand){
                  if(subRecord["categoria"] === product.category){
                    categoryReference += 1;
                    subRecord["venta_total"] += Number(product.total?.toFixed(2));
                    subRecord["cant_productos"] += product.quantity;
                  }
                }
              });
              if(categoryReference > 0){
                subRecord["cant_ordenes"] += 1;
              }
            }
          }
        }
      });
    });

    return recordsCategories;

  }, [data]);

  const exportDataDaily = transformedDataDaily?.map((item) => {

    const row = { ...item };
    row["venta_total"] = `S/ ${item["venta_total"]?.toFixed(2)}`;
    row["ticketPromedio"] = item["cant_ordenes"] > 0 ? (item["cant_productos"] / item["cant_ordenes"])?.toFixed(2) : 0;
    return row;
  });

  const exportData = transformedData?.map((item) => {

    const row = { ...item };
    row["venta_total"] = `S/ ${item["venta_total"]?.toFixed(2)}`;
    row["ticketPromedio"] = item["cant_ordenes"] > 0 ? (item["cant_productos"] / item["cant_ordenes"])?.toFixed(2) : 0;
    return row;
  });

  const exportColumnsDaily = [
    { title: "Fecha", dataIndex: "fecha", key: "fecha", width: 150 },
    { title: "Vendedor", dataIndex: "vendedor", key: "vendedor", width: 150 },
    { title: "Canal cierre", dataIndex: "canal_cierre", key: "canal_cierre", width: 150 },
    { title: "Marca", dataIndex: "marca", key: "marca", width: 150},
    { title: "Categoria", dataIndex: "categoria", key: "categoria", width: 150},
    { title: "Venta total", dataIndex: "venta_total", key: "venta_total", width: 150 },
    { title: "Cantidad ordenes", dataIndex: "cant_ordenes", key: "cant_ordenes", width: 100 },
    { title: "Cantidad productos", dataIndex: "cant_productos", key: "cant_productos", width: 100 },
    { title: "Ticket Promedio", dataIndex: "ticketPromedio", key: "ticketPromedio", width: 100 }
  ];

  const exportColumns = [
    { title: "Vendedor", dataIndex: "vendedor", key: "vendedor", width: 150 },
    { title: "Canal cierre", dataIndex: "canal_cierre", key: "canal_cierre", width: 150 },
    { title: "Categoria", dataIndex: "categoria", key: "categoria", width: 150},
    { title: "Venta total", dataIndex: "venta_total", key: "venta_total", width: 150 },
    { title: "Cantidad ordenes", dataIndex: "cant_ordenes", key: "cant_ordenes", width: 100 },
    { title: "Cantidad productos", dataIndex: "cant_productos", key: "cant_productos", width: 100 },
    { title: "Ticket promedio", dataIndex: "ticketPromedio", key: "ticketPromedio", width: 100 }
  ];

  const colors = [
    "#244861",
    "#275F6F",
    "#2B767E",
    "#28777E",
    "#2F8E8D",
    "#2F979A",
    "#34B4C1",
    "#3ECED6",
    "#4DDCE3",
    "#6AE7ED",
  ];

  const sortedChannelData = Object.values(closingChannelData)?.sort((a, b) => b.value - a.value);

  const barData = sortedChannelData?.map((d, i) => ({
    ...d,
    fill: colors[i % colors.length],
    Ventas: d.value.toFixed(2),
  }));

  const maxSale = Math?.max(...barData?.map((d) => d.value));
  const yAxisDomain = [0, Math.ceil(maxSale / 1000) * 1000];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            padding: "10px",
          }}
        >
          <b className="intro">{label}</b>
          <p className="intro">{`Ventas totales: S/ ${payload[0].payload.value?.toFixed(
            2
          )}`}</p>
          <p className="desc">{`Ventas entregadas: S/ ${payload[0].payload.deliveredSales?.toFixed(
            2
          )}`}</p>
          <p className="desc">{`Cantidad de ordenes: ${payload[0].payload.totalOrders}`}</p>
          <p className="desc">{`Cantidad de productos: ${payload[0].payload.totalProducts}`}</p>
          <p className="desc">{`Ticket promedio: ${payload[0].payload.averageTicket?.toFixed(
            2
          )}`}</p>
        </div>
      );
    }

    return null;
  };

  const renderCustomizedLabel = (props) => {
    const { x, y, width, value } = props;
    const radius = 10;

    return (
      <text
        x={x + width / 2}
        y={y - radius}
        fill="#000"
        textAnchor="middle"
        dominantBaseline="middle"
        style={{ fontSize: "12px" }}
      >
        {`S/ ${value}`}
      </text>
    );
  };

  return (
    <Card title="Canales de cierre">
      <ResponsiveContainer width="100%" height={415}>
        <BarChart
          data={barData}
          margin={{
            top: 20,
            right: 15,
            left: 0,
            bottom: 10,
          }}
        >
          <XAxis dataKey="name" />
          <YAxis domain={yAxisDomain} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="Ventas" fill="#244861">
            <LabelList content={renderCustomizedLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 10,
          marginRight: 20,
        }}
      >
        <ExportToExcel
          data={exportData}
          fileName="ventasVendedorCanalCierre"
          columns={exportColumns}
          name={"Detalle general"}
          showView={false}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 10,
          marginRight: 20,
        }}
      >
        <ExportToExcel
          data={exportDataDaily}
          fileName="ventasDiariasVendedorCanalCierre"
          columns={exportColumnsDaily}
          name={"Detalle diario"}
          showView={false}
        />
      </div>
    </Card>
  );
};
